// src/talent/components/NoticePreviewDialog.js
import React, { useEffect, useState } from "react";
import {
  Dialog,
  Typography,
  Button,
  Box,
  ButtonBase,
  Paper,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";
import { useLocationLabels } from "../../hooks/useLocationLabels";
import { addSpaceAfterCommas } from "../../utils/textUtils";
import AppLoader from "../../components/common/AppLoader";

// --- shared helpers (matches NoticeDetailPage)
const hasValue = (v) => {
  if (v === null || v === undefined) return false;

  const str = String(v).trim();

  if (
    str === "" ||
    /^0+$/.test(str) ||
    /^0+(\.0+)?$/.test(str)
  ) {
    return false;
  }

  return true;
};

const Pipe = () => <span style={{ color: "#ccc", margin: "0 4px" }}>|</span>;
const Label = ({ children }) => (
  <span style={{ color: "#000", fontWeight: 500 }}>{children}</span>
);

// --- FileLink (matches NoticeDetailPage)
function FileLink({ url, label }) {
  if (!url) return null;
  const lower = url.toLowerCase();
  const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(lower);
  const Icon = isImage ? ImageIcon : DescriptionIcon;
  return (
    <ButtonBase
      onClick={() => window.open(url, "_blank", "noopener")}
      sx={{ display: "flex", alignItems: "center", mt: 1.5 }}
    >
      <Icon fontSize="medium" sx={{ mr: 1, color: "common.black" }} />
      <Typography
        variant="body2"
        noWrap
        sx={{ color: "common.black", textDecoration: "none" }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

function formatHeight(inches) {
  if (!hasValue(inches)) return null;
  const ft = Math.floor(inches / 12);
  const rem = inches % 12;
  return `${ft}′${rem}″`;
}

export default function NoticePreviewDialog({ open, onClose, notice }) {
  const [loading, setLoading] = useState(false);
  const [fullNotice, setFullNotice] = useState(null);
  const [roles, setRoles] = useState([]);
  const { formatLocations, loading: locLoading } = useLocationLabels();

  useEffect(() => {
    if (!open || !notice) return;
    (async () => {
      setLoading(true);
      try {
        const email = localStorage.getItem("email");

        // fetch notice details
        const noticeRes = await api.get(
          ENDPOINTS.getNoticeDetails({ email, noticeId: notice.noticeId }),
          { params: { email, page: 1, pageSize: 5000 } }
        );
        const found = (noticeRes.data?.data || []).find(
          (n) => Number(n.noticeId) === Number(notice.noticeId)
        );
        setFullNotice(found || null);

        // fetch roles
        const rolesRes = await api.get(
          ENDPOINTS.getRoleDetails({
            noticeId: notice.noticeId,
            page: 1,
            pageSize: 5000,
          })
        );
        setRoles(rolesRes.data?.data?.items || []);
      } catch (err) {
        console.error("NoticePreview load failed:", err);
        setFullNotice(null);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, notice]);

  if (!open) return null;

  const parseIfJSON = (value) => {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(", ");
      if (typeof parsed === "object" && Object.keys(parsed).length === 0)
        return "";
      return parsed;
    } catch {
      return value;
    }
  };

  const hasShortDesc =
    fullNotice?.castingOrShootDatesInfo &&
    fullNotice.castingOrShootDatesInfo.trim().length > 0;

  const hasFiles =
    fullNotice?.scriptsPath ||
    fullNotice?.picturesandDocumentsPath ||
    fullNotice?.photoReferencesPath;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box className="backgroundColor">
        <Box
          sx={{
            maxWidth: 1200,
            m: "auto",
            boxShadow: 3,
            borderRadius: 2,
            overflow: "hidden",
            p: 3,
          }}
        >
          {/* Header (no black loading placeholder; only render when data available) */}
          {!loading && !!fullNotice && (
            <Paper
              square
              elevation={0}
              sx={{
                bgcolor: "#202020",
                color: "white",
                borderRadius: "8px",
                p: 2,
                mb: 2,
                position: "relative",
              }}
            >
              {fullNotice?.rushCall && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "32px",
                    backgroundColor: "#d17c7c",
                    color: "#fff",
                    writingMode: "sideways-lr",
                    textOrientation: "mixed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: "14px",
                    letterSpacing: "1px",
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  }}
                >
                  Rush Call
                </Box>
              )}

              <Box sx={{ marginLeft: fullNotice?.rushCall ? "48px" : 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 400 }}>
                  {fullNotice?.projectTitle || "Untitled Project"}
                </Typography>

                <Typography variant="body2" sx={{ mt: 1, fontSize: "13px" }}>
                  <span style={{ color: "#ccc" }}>Seeking Talent From: </span>
                  <span style={{ color: "#90b5fe" }}>
                    {formatLocations(fullNotice?.auditionLocations || [])}
                  </span>
                </Typography>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  <span style={{ color: "#ccc" }}>Union Status:</span>{" "}
                  {addSpaceAfterCommas(fullNotice?.unionStatus || "N/A")}
                </Typography>

                <Typography variant="body2" sx={{ mt: 0 }}>
                  <span style={{ color: "#ccc" }}>Pay:</span>{" "}
                  {fullNotice?.payScale || "N/A"}
                </Typography>
<Typography variant="body2" sx={{ mt: 0 }}>
  {(() => {
    const createdDate = fullNotice?.noticeCreatedDate
      ? fullNotice.noticeCreatedDate.split("T")[0]
      : null;

    const updatedDate = fullNotice?.noticeUpdatedDate
      ? fullNotice.noticeUpdatedDate.split("T")[0]
      : null;

    const showUpdated =
      createdDate &&
      updatedDate &&
      updatedDate !== "N/A" &&
      updatedDate > createdDate;

    return (
      <>
        <span style={{ color: "#ccc" }}>Posted:</span>{" "}
        <span style={{ color: "#90b5fe" }}>
          {fullNotice?.noticeCreatedDate
            ? new Date(fullNotice.noticeCreatedDate).toLocaleDateString()
            : "N/A"}
        </span>

        {showUpdated && (
          <>
            <span style={{ color: "#ccc" }}> Updated:</span>{" "}
            <span style={{ color: "#90b5fe" }}>
              {new Date(fullNotice.noticeUpdatedDate).toLocaleDateString()}
            </span>
          </>
        )}

        <span style={{ color: "#ccc", paddingLeft: "5px" }}>
          Deadline for Submissions:
        </span>{" "}
        <span style={{ color: "#d17c7c" }}>
          {fullNotice?.noticeEndDate
            ? new Date(fullNotice.noticeEndDate).toLocaleDateString()
            : "N/A"}
        </span>
      </>
    );
  })()}
</Typography>
              </Box>
            </Paper>
          )}

          {/* Content */}
          <Box sx={{ bgcolor: "background.paper", p: 2 }}>
            {loading || locLoading || !fullNotice ? (
              <AppLoader message="Loading casting data..." />
            ) : (
              <>
                {/* Description */}
                {(fullNotice?.projectDescription || "")
                  .split(/\r\n\r\n/)
                  .filter(Boolean)
                  .map((para, idx) => (
                    <Typography
                      key={idx}
                      paragraph
                      component="div"
                      sx={{ fontSize: "1rem" }}
                      dangerouslySetInnerHTML={{ __html: para }}
                    />
                  ))}

                {/* Shoot / Casting Info */}
                {hasShortDesc && (
                  <Box sx={{ p: 1, backgroundColor: "#eee", my: 1 }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      Casting / Shoot Info
                    </Typography>

                    <Typography
                      component="div"
                      sx={{ fontSize: "1rem" }}
                      dangerouslySetInnerHTML={{
                        __html: (
                          fullNotice.castingOrShootDatesInfo || ""
                        ).replace(/\r\n/g, "<br/>"),
                      }}
                    />
                  </Box>
                )}

                {/* Attachments */}
                {hasFiles && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, pt: 1 }}>
                      Attached Media:
                    </Typography>

                    <FileLink
                      url={fullNotice?.scriptsPath}
                      label="Script Reference"
                    />

                    <FileLink
                      url={fullNotice?.picturesandDocumentsPath}
                      label="Photo Reference"
                    />

                    <FileLink
                      url={fullNotice?.photoReferencesPath}
                      label="Photo References"
                    />
                  </Box>
                )}

                {/* Roles */}
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    mt: 2,
                  }}
                >
                  {roles.map((role, index) => (
                    <Box
                      key={role.roleId}
                      sx={{
                        my: 1,
                        mb: 1,
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        borderBottom:
                          index !== roles.length - 1
                            ? "1px solid #eee"
                            : "none",
                        pb: 2,
                        flexDirection: "row",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        {/* --- Role Header --- */}
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 400 }}>
                          <span style={{ fontWeight: 500, color: "#90b5fe" }}>
                            {role.roleName}
                          </span>

                          {role.sex && (
                            <>
                              <Pipe />
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(role.sex)}
                              </span>
                            </>
                          )}
                          {role.ethnicity && (
                            <>
                              <Pipe />
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(role.ethnicity)}
                              </span>
                            </>
                          )}
                          {(hasValue(role.ageStart) ||
                            hasValue(role.ageEnd)) && (
                            <>
                              <Pipe />
                              <Label>Age: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.ageStart}-{role.ageEnd}
                              </span>
                            </>
                          )}
                          {role.roleType && (
                            <>
                              <Pipe />
                              <Label>Role Type: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.roleType}
                              </span>
                            </>
                          )}
                          {role.roleUnion && (
                            <>
                              <Pipe />
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(role.roleUnion)}
                              </span>
                            </>
                          )}
                        </Typography>

                        {/* --- Measurements + Vocal --- */}
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 400 }}>
                          {role.eyeColor && (
                            <>
                              <Label>Eye Color: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(role.eyeColor)}
                              </span>
                            </>
                          )}

                          {role.hairColor && (
                            <>
                              <Pipe />
                              <Label>Hair Color: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(role.hairColor)}
                              </span>
                            </>
                          )}

                          {(hasValue(role.heightStart) ||
                            hasValue(role.heightEnd)) && (
                            <>
                              <Pipe />
                              <Label>Height: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {formatHeight(role.heightStart)} –{" "}
                                {formatHeight(role.heightEnd)}
                              </span>
                            </>
                          )}

                          {(hasValue(role.weightStart) ||
                            hasValue(role.weightEnd)) && (
                            <>
                              <Pipe />
                              <Label>Weight: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.weightStart}-{role.weightEnd} lbs
                              </span>
                            </>
                          )}

                          {(hasValue(role.suitOne) ||
                            hasValue(role.suitTwo)) && (
                            <>
                              <Pipe />
                              <Label>Suit: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.suitOne}-{role.suitTwo}
                              </span>
                            </>
                          )}

                          {hasValue(role.shirt) && (
                            <>
                              <Pipe />
                              <Label>Shirt: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.shirt}
                              </span>
                            </>
                          )}

                          {(hasValue(role.neckOne) ||
                            hasValue(role.neckTwo)) && (
                            <>
                              <Pipe />
                              <Label>Neck: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.neckOne}-{role.neckTwo}″
                              </span>
                            </>
                          )}

                          {(hasValue(role.sleeveOne) ||
                            hasValue(role.sleeveTwo)) && (
                            <>
                              <Pipe />
                              <Label>Sleeve: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.sleeveOne}-{role.sleeveTwo}″
                              </span>
                            </>
                          )}

                          {(hasValue(role.waistOne) ||
                            hasValue(role.waistTwo)) && (
                            <>
                              <Pipe />
                              <Label>Waist: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.waistOne}-{role.waistTwo}″
                              </span>
                            </>
                          )}

                          {(hasValue(role.inseamOne) ||
                            hasValue(role.inseamTwo)) && (
                            <>
                              <Pipe />
                              <Label>Inseam: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.inseamOne}-{role.inseamTwo}″
                              </span>
                            </>
                          )}

                          {(hasValue(role.shoeOne) ||
                            hasValue(role.shoeTwo)) && (
                            <>
                              <Pipe />
                              <Label>Shoe: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.shoeOne}-{role.shoeTwo}
                              </span>
                            </>
                          )}

                          {(hasValue(role.dressOne) ||
                            hasValue(role.dressTwo)) && (
                            <>
                              <Pipe />
                              <Label>Dress: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.dressOne}-{role.dressTwo}
                              </span>
                            </>
                          )}

                          {(hasValue(role.bustOne) ||
                            hasValue(role.bustTwo)) && (
                            <>
                              <Pipe />
                              <Label>Bust: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.bustOne}-{role.bustTwo}
                              </span>
                            </>
                          )}

                          {hasValue(role.cup) && (
                            <>
                              <Pipe />
                              <Label>Cup: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.cup}
                              </span>
                            </>
                          )}

                          {(hasValue(role.femalWaistOne) ||
                            hasValue(role.femalWaistTwo)) && (
                            <>
                              <Pipe />
                              <Label>Female Waist: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.femalWaistOne}-{role.femalWaistTwo}
                              </span>
                            </>
                          )}

                          {(hasValue(role.hipsOne) ||
                            hasValue(role.hipsTwo)) && (
                            <>
                              <Pipe />
                              <Label>Hips: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.hipsOne}-{role.hipsTwo}
                              </span>
                            </>
                          )}

                          {(hasValue(role.femaleShoeOne) ||
                            hasValue(role.femaleShoeTwo)) && (
                            <>
                              <Pipe />
                              <Label>Female Shoe: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.femaleShoeOne}-{role.femaleShoeTwo}
                              </span>
                            </>
                          )}

                          {hasValue(role.childSize) && (
                            <>
                              <Pipe />
                              <Label>Child Size: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.childSize}
                              </span>
                            </>
                          )}

                          {(hasValue(role.childShoeOne) ||
                            hasValue(role.childShoeTwo)) && (
                            <>
                              <Pipe />
                              <Label>Child Shoe: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.childShoeOne}-{role.childShoeTwo}
                              </span>
                            </>
                          )}

                          {hasValue(role.otherMeasurements) && (
                            <>
                              <Pipe />
                              <Label>Other: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {role.otherMeasurements}
                              </span>
                            </>
                          )}

                          {role.vocalRange && (
                            <>
                              <Pipe />
                              <Label>Vocal Range: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(
                                  parseIfJSON(role.vocalRange)
                                )}
                              </span>
                            </>
                          )}

                          {role.vocalStyle && (
                            <>
                              <Pipe />
                              <Label>Vocal Style: </Label>
                              <span style={{ fontWeight: 500 }}>
                                {addSpaceAfterCommas(
                                  parseIfJSON(role.vocalStyle)
                                )}
                              </span>
                            </>
                          )}
                        </Typography>

                        {/* --- Role Details --- */}
                        <Typography
                          sx={{ fontSize: "16px" }}
                          component="div"
                          dangerouslySetInnerHTML={{
                            __html: role.moreAboutRole
                              ? role.moreAboutRole.replace(/\r\n/g, "<br/>")
                              : "",
                          }}
                        />

                        {/* --- Attachments --- */}
                        {role.photoorScriptPath && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <FileLink
                              url={role.photoorScriptPath}
                              label="Role Reference"
                            />
                          </Box>
                        )}

                        {/* --- Reel Requirements --- */}
                        <Typography sx={{ mt: 0.5, fontSize: "13px" }}>
                          {role.reelRequired === 1 && (
                            <>
                              <span
                                style={{ color: "#d17c7c", fontWeight: 500 }}
                              >
                                Video Reel Required
                              </span>
                            </>
                          )}
                          {role.audioReelRequired === 1 && (
                            <>
                              <Pipe />
                              <span
                                style={{ color: "#d17c7c", fontWeight: 500 }}
                              >
                                Audio Reel Required
                              </span>
                            </>
                          )}
                        </Typography>

                        {/* --- Pay --- */}
                        {role.payment && (
                          <Typography variant="body1">
                            <Label>Pay: </Label>
                            <span style={{ fontWeight: 500 }}>
                              {role.payment}
                            </span>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Close */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Button onClick={onClose} variant="outlined" color="primary">
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}